import { api } from '@connected/api-client';
import { elektraAppQueryOptions } from '@connected/elektra-sdk/queries/query-options';
import { portaalQueryOptions } from '@connected/portaal-sdk/queries/options';
import { Button } from '@connected/ui/components/button';
import { useQueries, useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { Edit } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { LoadingPage } from '@/components/loading-page';
import { ScrollableTable, ScrollableTableBody, ScrollableTableCell, ScrollableTableHead, ScrollableTableHeader, ScrollableTableRow } from '@/components/scrollable-table';
import { type UserData, UserSettingsModal } from '@/features/user-settings/components/user-settings-modal';
import { queryClient } from '@/lib/query-client';

export const Route = createFileRoute(
    '/_auth/dashboard/organizations/$organizationId/(admin)/validation/',
)({
    component: RouteComponent,
});

function RouteComponent() {
    const { t } = useTranslation('dashboard');

    const params = Route.useParams();

    const [isEditModalOpen, setEditModalOpen] = useState(false);
    const [selectedUser, setSelectedUser] = useState<UserData | null>(null);

    // check if details
    const organisationInfo = useQuery({ ...portaalQueryOptions.organizations.view({
        orgainsationId: params.organizationId,
    }) });

    // check if logo
    const viewLogos = useQuery({ ...portaalQueryOptions.organizations.logo({
        orgainsationId: params.organizationId,
        ImageQuality: 'ExtraSmall',
    }) });

    // check if active norm list
    const norms = useQuery({
        ...portaalQueryOptions.norms.ListElectricalInspectionActiveNormList({
            organizationId: params.organizationId,
        }),
    });

    // check if visual inspections
    const visualInspections = useQuery({
        ...elektraAppQueryOptions.organisation.management.checkListOptions({
            organisationId: params.organizationId,
        }),
    });

    // check if users exist
    const userQuery = useQuery({
        ...portaalQueryOptions.users.userIdsOptions({
            organisationId: params.organizationId,
        }),
    });

    const roles = useQuery(elektraAppQueryOptions.organisation.management.organisationRolesGetOptions({
        organisationId: params.organizationId,
    }));

    const filesQuery = useQueries({
        queries: (userQuery?.data?.Users ?? []).map(user => ({
            ...portaalQueryOptions.electrical.users.userFiles({
                userId: user.UserId ?? '',
            }),
            enabled: !!user.UserId,
        })),
    });

    const users = useMemo(() => {
        if (userQuery?.isSuccess && userQuery?.isFetched) {
            return userQuery?.data?.Users?.map(user => ({
                ...user,
                hasSignature: filesQuery?.find(query => query.data?.Files.some(file => file.UserId === user.UserId && file?.Description === 'userSignature'))?.data?.Files ?? [],
                isInspector: user.Roles?.some(role => role.Name?.toLowerCase().includes('inspecteur')) ?? false,
                isManager: user.Roles?.some(role => role.Name?.toLowerCase().includes('manager')) ?? false,
                isOwner: user.Roles?.some(role => role.Name?.toLowerCase().includes('eigenaar')) ?? false,
            }));
        }
        return [];
    }, [userQuery?.isSuccess, userQuery?.isFetched, userQuery?.data?.Users, filesQuery]);

    const createDefaultNorms = api.useMutation('post', '/api/v2/electrical/inspectionnorms/lists/create');
    const activateNormsList = api.useMutation('put', '/api/v2/electrical/inspectionnorms/lists/activate');

    const createVisualInspections = api.useMutation('post', '/api/v2/electrical/checklists/lists/create');
    const activateVisualInspections = api.useMutation('put', '/api/v2/electrical/checklists/lists/activate');

    function handleEditModalSuccess() {
        queryClient.invalidateQueries(
            portaalQueryOptions.users.userIdsOptions({
                organisationId: params.organizationId,
            }),
        );
    }

    if (organisationInfo?.isLoading || viewLogos?.isLoading || norms?.isLoading || visualInspections?.isLoading || userQuery?.isLoading) {
        return <LoadingPage />;
    }
    //

    return (
        <div className="flex flex-col lg:flex-wrap gap-8 justify-start">
            <div className="flex flex-col sm:flex-row justify-between">
                <div className="flex flex-col gap-1">
                    <h3 className="text-2xl font-semibold">
                        {t('dashboard.admin.validation.title')}
                    </h3>
                    <p className="text-md text-text-primary/70">
                        {t('dashboard.admin.validation.subtitle')}
                    </p>
                </div>
            </div>
            <div className="grid grid-cols-1 xl:grid-cols-3 gap-3">
                <div className="border col-span-1 rounded-lg p-6 flex-1">
                    <div className="flex flex-row items-center justify-between">
                        <h2 className="text-lg font-semibold">
                            {t(
                                'dashboard.admin.validation.organisation-details.title',
                            )}
                        </h2>
                    </div>
                    <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
                        <div>
                            <p className="text-sm font-semibold mb-1">
                                {t(
                                    'dashboard.admin.validation.organisation-details.companyName',
                                )}
                            </p>
                            <p className="text-sm">
                                {organisationInfo.data?.Organization?.Name || <span className="text-red-600">MISSING</span>}
                            </p>
                        </div>
                        <div>
                            <p className="text-sm font-semibold mb-1">
                                {t(
                                    'dashboard.admin.validation.organisation-details.phoneNumber',
                                )}
                            </p>
                            <p className="text-sm">
                                {organisationInfo.data?.Organization?.TelephoneNumber || <span className="text-red-600">MISSING</span>}
                            </p>
                        </div>
                        <div>
                            <p className="text-sm font-semibold mb-1">
                                {t(
                                    'dashboard.admin.validation.organisation-details.website',
                                )}
                            </p>
                            <p className="text-sm">
                                {organisationInfo.data?.Organization?.Website || <span className="text-red-600">MISSING</span>}
                            </p>
                        </div>
                        <div>
                            <p className="text-sm font-semibold mb-1">
                                {t(
                                    'dashboard.admin.validation.organisation-details.logo',
                                )}
                            </p>
                            <p className="text-sm">
                                {viewLogos?.status === 'success' ? 'Beschikbaar' : <span className="text-red-600">MISSING</span>}
                            </p>
                        </div>
                        <div>
                            <p className="text-sm font-semibold mb-1">
                                {t(
                                    'dashboard.admin.validation.organisation-details.kvkNumber',
                                )}
                            </p>
                            <p className="text-sm">
                                {organisationInfo.data?.Organization?.BusinessRegistration?.Number || <span className="text-red-600">MISSING</span>}
                            </p>
                        </div>
                        <div>
                            <p className="text-sm font-semibold mb-1">
                                {t(
                                    'dashboard.admin.validation.organisation-details.SCIOSRegistrationNumber',
                                )}
                            </p>
                            <p className="text-sm">
                                {organisationInfo.data?.Organization?.SciosRegistrationNumber || <span className="text-red-600">MISSING</span>}
                            </p>
                        </div>
                        <div>
                            <p className="text-sm font-semibold mb-1">
                                {t(
                                    'dashboard.admin.validation.organisation-details.companyEmail',
                                )}
                            </p>
                            <p className="text-sm">
                                {organisationInfo.data?.Organization?.Email || <span className="text-red-600">MISSING</span>}
                            </p>
                        </div>
                        <div>
                            <p className="text-sm font-semibold mb-1">
                                {t(
                                    'dashboard.admin.validation.organisation-details.companyPhone',
                                )}
                            </p>
                            <p className="text-sm">
                                {/* {organisationInfo.data?.Organization?.TelephoneNumber || <span className="text-red-600">NIET IN</span>} */}
                                -
                            </p>
                        </div>
                        <div>
                            <p className="text-sm font-semibold mb-1">
                                {t(
                                    'dashboard.admin.validation.organisation-details.IBAN',
                                )}
                            </p>
                            <p className="text-sm">
                                {organisationInfo.data?.Organization?.IBAN || <span className="text-red-600">MISSING</span>}
                            </p>
                        </div>
                        <div>
                            <p className="text-sm font-semibold mb-1">
                                BIC
                            </p>
                            <p className="text-sm">
                                {organisationInfo.data?.Organization?.BIC || <span className="text-red-600">MISSING</span>}
                            </p>
                        </div>
                        <div>
                            <p className="text-sm font-semibold mb-1">
                                BTW
                            </p>
                            <p className="text-sm">
                                {organisationInfo.data?.Organization?.VatNumber || <span className="text-red-600">MISSING</span>}
                            </p>
                        </div>
                        <div>
                            <p className="text-sm font-semibold mb-1">
                                Offerte
                            </p>
                            <p className="text-sm">
                                {organisationInfo.data?.Organization?.EnableElectricalProjectPriceQuote ? 'Enabled' : <span className="text-red-600">Disabled</span>}
                            </p>
                        </div>
                        <div>
                            <p className="text-sm font-semibold mb-1">
                                Planning
                            </p>
                            <p className="text-sm">
                                {organisationInfo.data?.Organization?.EnableElectricalProjectPlanning ? 'Enabled' : <span className="text-red-600">Disabled</span>}
                            </p>
                        </div>
                        <div>
                            <p className="text-sm font-semibold mb-1">
                                Con. op aanbevelingen
                            </p>
                            <p className="text-sm">
                                {organisationInfo.data?.Organization?.EnableElectricalProjectRecommendations ? 'Enabled' : <span className="text-red-600">Disabled</span>}
                            </p>
                        </div>
                    </div>
                </div>
                <div className="border col-span-1 xl:col-span-2 rounded-lg p-6 flex-1">
                    <div className="flex flex-row items-center justify-between">
                        <h2 className="text-lg font-semibold">
                            Gebruikers
                        </h2>
                        <p className="text-xs">Gebruikers die geen inspecteur zijn hebben geen missing for kleurcode</p>
                    </div>
                    <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
                        <div className="w-full sm:col-span-2 rounded-2xl border overflow-hidden">
                            {users?.length > 0 && (
                                <ScrollableTable className="w-full" maxHeight={20}>
                                    <ScrollableTableHeader className="bg-background-secondary">
                                        <ScrollableTableRow className="h-12">
                                            <ScrollableTableHead className="text-text-primary/70 w-32">
                                                Naam
                                            </ScrollableTableHead>
                                            <ScrollableTableHead className="text-text-primary/70 bg-background-secondary px-4">
                                                Handtekening
                                            </ScrollableTableHead>
                                            <ScrollableTableHead className="text-text-primary/70">
                                                Email
                                            </ScrollableTableHead>
                                            <ScrollableTableHead className="text-text-primary/70 bg-background-secondary px-4">
                                                Telefoonnummer
                                            </ScrollableTableHead>
                                            <ScrollableTableHead className="text-text-primary/70">
                                                Kleur
                                            </ScrollableTableHead>
                                            <ScrollableTableHead className="text-text-primary/70 bg-background-secondary px-4">
                                                Rol
                                            </ScrollableTableHead>
                                            <ScrollableTableHead className="text-text-primary/70 w-12">
                                            </ScrollableTableHead>
                                        </ScrollableTableRow>
                                    </ScrollableTableHeader>
                                    <ScrollableTableBody>
                                        {users.map(user => (
                                            <ScrollableTableRow key={user.UserId}>
                                                <ScrollableTableCell className="whitespace-nowrap">{user.Settings?.DisplayName}</ScrollableTableCell>
                                                <ScrollableTableCell>{user.hasSignature.length > 0 ? 'Beschikbaar' : <span className="text-red-600">MISSING</span>}</ScrollableTableCell>
                                                <ScrollableTableCell className="break-all">{user.Email}</ScrollableTableCell>
                                                <ScrollableTableCell className="whitespace-nowrap">{user.Settings?.PhoneNumber}</ScrollableTableCell>
                                                <ScrollableTableCell>
                                                    {user.isInspector && !user.Settings?.ColorCode
                                                        ? <span className="text-red-600">MISSING</span>
                                                        : user.Settings?.ColorCode}
                                                </ScrollableTableCell>
                                                <ScrollableTableCell>{user.Roles?.map(role => role.Name).join(', ')}</ScrollableTableCell>
                                                <ScrollableTableCell>
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        className="h-8 w-8"
                                                        onClick={() => {
                                                            setSelectedUser(user);
                                                            setEditModalOpen(true);
                                                        }}
                                                    >
                                                        <Edit className="text-text-primary/40 size-4" />
                                                    </Button>
                                                </ScrollableTableCell>
                                            </ScrollableTableRow>
                                        ))}
                                    </ScrollableTableBody>
                                </ScrollableTable>
                            )}
                            {users.length === 0 && <span className="text-red-600">MISSING</span>}
                        </div>
                    </div>
                </div>
                <div className="border col-span-1 rounded-lg p-6 flex-1">
                    <div className="flex flex-row items-center justify-between">
                        <h2 className="text-lg font-semibold">
                            Normen
                        </h2>
                    </div>
                    <div>
                        {norms?.isSuccess && (norms.data?.Count ?? 0) > 0 && (
                            <div>
                                <p className="text-green-700">Er is een normlijst beschikbaar</p>
                            </div>
                        )}
                        {norms?.isFetched && norms.data?.Count === 0 && (
                            <div>
                                <Button onClick={async () => {
                                    await createDefaultNorms.mutateAsync({
                                        body: {
                                            OrganizationId: params.organizationId,
                                            Description: 'Standard List',
                                        },
                                    }).then((response) => {
                                        activateNormsList.mutateAsync({
                                            body: {
                                                ElectricalInspectionNormListId: response?.ElectricalInspectionNormListId,
                                            },
                                        });
                                        queryClient.invalidateQueries(
                                            portaalQueryOptions.norms.ListElectricalInspectionActiveNormList({
                                                organizationId: params.organizationId,
                                            }),
                                        );
                                        toast.success('Normlijst aangemaakt. Refresh als nodig.');
                                    });
                                }}
                                >
                                    Normlijst aanmaken
                                </Button>
                            </div>
                        )}
                    </div>
                </div>
                <div className="border col-span-1 rounded-lg p-6 flex-1">
                    <div className="flex flex-row items-center justify-between">
                        <h2 className="text-lg font-semibold">
                            Visuele Inspectie / Scope 12 Lijst
                        </h2>
                    </div>
                    <div>
                        {visualInspections?.isSuccess && (visualInspections.data?.Count ?? 0) > 0 && (
                            <div>
                                <p className="text-green-700">Er is een checklist lijst beschikbaar</p>
                            </div>
                        )}
                        {visualInspections?.isFetched && (visualInspections.data?.Count) === 0 && (
                            <div>
                                <Button onClick={async () => {
                                    await createVisualInspections.mutateAsync({
                                        body: {
                                            OrganizationId: params.organizationId,
                                            Description: 'Standard List',
                                        },
                                    }).then((response) => {
                                        activateVisualInspections.mutateAsync({
                                            body: {
                                                ElectricalCheckListId: response?.ElectricalCheckListId,
                                            },
                                        });
                                        queryClient.invalidateQueries(
                                            elektraAppQueryOptions.organisation.management.checkListOptions({
                                                organisationId: params.organizationId,
                                            }),
                                        );
                                        toast.success('Visuele Inspectie / Scope 12 lijst aangemaakt aangemaakt. Refresh als nodig.');
                                    });
                                }}
                                >
                                    Visuele Inspectie Lijst aanmaken
                                </Button>
                            </div>
                        )}
                    </div>
                </div>

            </div>

            <UserSettingsModal
                isOpen={isEditModalOpen}
                setIsOpen={setEditModalOpen}
                roles={roles.data}
                user={selectedUser ?? undefined}
                organizationId={params.organizationId}
                onSuccess={handleEditModalSuccess}
            />
        </div>
    );
}
